import type { UpdateProfileBody, UserRoleEnum } from "@nlc/shared-types";
import type { Control } from "react-hook-form";

import type { UseAvatarUploadReturn } from "@/features/users/shared/hooks/useAvatarUpload";

import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";

import type { ProfileTabDefinition } from "../hooks/useProfileTabs";

import { ManageAccountTab } from "./tabs/ManageAccountTab";
import { PerformanceTab } from "./tabs/PerformanceTab";

interface ProfileTabsContainerProps {
  avatarUpload: UseAvatarUploadReturn;
  control: Control<UpdateProfileBody>;
  initials?: null | string;
  projects?: Array<{ id: number | string; name: string }>;
  roleDisplayName?: null | string;
  roleId?: UserRoleEnum;
  tabs: ProfileTabDefinition[];
  teamName?: null | string;
  userAvatar?: null | string;
  userId?: number | string;
  userName?: null | string;
}

function renderTabContent(
  tabId: ProfileTabDefinition["id"],
  props: Omit<ProfileTabsContainerProps, "tabs">,
) {
  const {
    avatarUpload,
    control,
    initials,
    projects,
    roleDisplayName,
    roleId,
    teamName,
    userAvatar,
    userId,
    userName,
  } = props;

  switch (tabId) {
    case "manage-account": {
      return (
        <ManageAccountTab
          avatarUpload={avatarUpload}
          control={control}
          initials={initials ?? undefined}
          projects={projects}
          roleDisplayName={roleDisplayName ?? undefined}
          roleId={roleId}
          teamName={teamName ?? undefined}
          userAvatar={userAvatar ?? undefined}
          userId={userId}
          userName={userName ?? undefined}
        />
      );
    }
    case "performance": {
      return <PerformanceTab projects={projects} userId={userId} />;
    }
    default: {
      return null;
    }
  }
}

export function ProfileTabsContainer({
  tabs,
  ...props
}: ProfileTabsContainerProps) {
  if (tabs.length <= 1) {
    return (
      <div className="px-xl py-5 lg:px-10">
        {renderTabContent(tabs[0]!.id, props)}
      </div>
    );
  }

  return (
    <Tabs defaultSelectedKey="manage-account">
      <div className="py-lg">
        <TabList className="px-xl lg:px-2xl flex overflow-x-auto">
          {tabs.map((tab) => (
            <Tab id={tab.id} key={tab.id}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
      </div>
      {tabs.map((tab) => (
        <TabPanel id={tab.id} key={tab.id}>
          <div className="px-xl py-5 lg:px-10">
            {renderTabContent(tab.id, props)}
          </div>
        </TabPanel>
      ))}
    </Tabs>
  );
}
